import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Recently viewed products section.
 * Reads product IDs from localStorage, fetches the section via search URL,
 * and morphs it into the DOM.
 */
class RecentlyViewedProducts extends Component {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();

    // Guard: already loaded (e.g. element re-connected after DOM morph)
    if (this.dataset.loaded === 'true') {
      this.#wireTopArrows();
      return;
    }

    const currentProductId = this.dataset.currentProductId;
    const viewedProducts = RecentlyViewed.getSectionProducts().filter(
      (/** @type {string} */ id) => id !== currentProductId
    );

    if (viewedProducts.length === 0) {
      this.style.display = 'none';
      return;
    }

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Wire top arrow buttons to the slideshow-component's navigation methods.
   */
  #wireTopArrows() {
    const topArrows = this.querySelector('.recently-viewed__top-arrows');
    if (!topArrows) return;

    /** @type {import('@theme/slideshow').Slideshow | null} */
    const slideshow = /** @type {any} */ (this.querySelector('slideshow-component'));
    if (!slideshow) return;

    /** @type {HTMLButtonElement | null} */
    const prevBtn = topArrows.querySelector('.recently-viewed__arrow--prev');
    /** @type {HTMLButtonElement | null} */
    const nextBtn = topArrows.querySelector('.recently-viewed__arrow--next');

    if (prevBtn) prevBtn.addEventListener('click', () => slideshow.previous());
    if (nextBtn) nextBtn.addEventListener('click', () => slideshow.next());

    // Update disabled state on slide changes
    const updateDisabled = () => {
      const slides = slideshow.slides;
      if (prevBtn) prevBtn.disabled = Boolean(!slideshow.infinite && slideshow.current === 0);
      if (nextBtn) nextBtn.disabled = Boolean(!slideshow.infinite && slides && slideshow.nextIndex >= slides.length);
    };

    slideshow.addEventListener('slideshow:select', updateDisabled);
    // Defer initial check to let slideshow initialize
    requestAnimationFrame(() => updateDisabled());
  }

  async #loadProducts() {
    const { sectionId } = this.dataset;
    if (!sectionId) return;

    const viewedProducts = RecentlyViewed.getSectionProducts();
    const currentProductId = this.dataset.currentProductId;

    // Filter out the current product
    const filteredProducts = currentProductId
      ? viewedProducts.filter((/** @type {string} */ id) => id !== currentProductId)
      : viewedProducts;

    if (filteredProducts.length === 0) {
      this.style.display = 'none';
      return;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', filteredProducts.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();

      if (!html?.trim().length) {
        this.style.display = 'none';
        return;
      }

      // Check if the search returned actual products or just the skeleton fallback.
      // When search finds 0 matching products the template renders skeleton items —
      // in that case we hide the section instead of morphing the skeleton in.
      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const newSection = fragment.getElementById(`shopify-section-${sectionId}`);
      if (!newSection || newSection.querySelector('.product-recommendations__skeleton-item')) {
        this.style.display = 'none';
        return;
      }

      this.dataset.loaded = 'true';
      await morphSection(sectionId, html, 'full', { injectStylesheet: true });
      this.#wireTopArrows();
    } catch (e) {
      if (/** @type {Error} */ (e).name !== 'AbortError') {
        console.error('[recently-viewed-products] Failed to load:', e);
        this.style.display = 'none';
      }
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
